import React from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter, Button } from '@heroui/react';
import { Pencil, Trash2, X } from 'lucide-react';

/**
 * CredentialDrawer — the side panel a document or certificate opens in.
 *
 * Legacy showed a preview beside the list on row click, and the pencil turned
 * that same panel into the form. This keeps that flow in a right-hand drawer:
 * `mode="view"` shows the record with Edit / Delete in the header,
 * `mode="edit"` shows the form with Cancel / Save in the footer. Full width on
 * a phone, where there is no backdrop to tap, so the close button is always
 * in the header (44px target).
 *
 * Lifted above the legacy `.site-top-menu` (z 1052) like WhatsNewDrawer.
 */
function CredentialDrawer({
	isOpen,
	onClose,
	icon: Icon,
	title,
	subtitle,
	mode = 'view',
	onEdit,
	onDelete,
	deleting = false,
	footer = null,
	children
}) {
	return (
		<Drawer
			isOpen={isOpen}
			onClose={onClose}
			placement="right"
			size="lg"
			hideCloseButton
			scrollBehavior="inside"
			classNames={{ wrapper: '!z-[1060]', backdrop: '!z-[1060]', base: '!rounded-r-none credential-drawer' }}
		>
			<DrawerContent>
				<DrawerHeader className="flex items-start justify-between gap-3 border-b border-default-200">
					<div className="flex items-center gap-3 min-w-0">
						{Icon ? (
							<span className="w-9 h-9 shrink-0 rounded-lg bg-primary-500/10 flex items-center justify-center">
								<Icon className="w-[18px] h-[18px] text-primary-600" aria-hidden="true" />
							</span>
						) : null}
						<div className="flex flex-col min-w-0">
							<span className="text-[16px] font-semibold text-foreground truncate">{title}</span>
							{subtitle ? (
								<span className="text-[12.5px] font-normal text-default-500 truncate">{subtitle}</span>
							) : null}
						</div>
					</div>
					<div className="flex items-center gap-1 shrink-0">
						{mode === 'view' && onEdit ? (
							<Button
								isIconOnly
								variant="light"
								radius="full"
								aria-label="Edit"
								onPress={onEdit}
								className="min-w-11 w-11 h-11"
							>
								<Pencil className="w-[18px] h-[18px]" />
							</Button>
						) : null}
						{mode === 'view' && onDelete ? (
							<Button
								isIconOnly
								variant="light"
								color="danger"
								radius="full"
								aria-label="Delete"
								isLoading={deleting}
								onPress={onDelete}
								className="min-w-11 w-11 h-11"
							>
								<Trash2 className="w-[18px] h-[18px]" />
							</Button>
						) : null}
						<Button
							isIconOnly
							variant="light"
							radius="full"
							aria-label="Close"
							onPress={onClose}
							className="min-w-11 w-11 h-11"
						>
							<X className="w-5 h-5" />
						</Button>
					</div>
				</DrawerHeader>

				<DrawerBody className="gap-4 py-5">{children}</DrawerBody>

				{footer ? <DrawerFooter className="border-t border-default-200">{footer}</DrawerFooter> : null}
			</DrawerContent>
		</Drawer>
	);
}

/** One label/value line of a preview; renders nothing without a value. */
export function PreviewRow({ label, children }) {
	if (children == null || children === '' || children === false) return null;
	return (
		<div className="grid grid-cols-[minmax(110px,40%)_1fr] gap-3 py-2.5 border-b border-default-100 last:border-b-0">
			<dt className="text-[13px] text-default-500 m-0">{label}</dt>
			<dd className="text-[14px] text-foreground m-0 min-w-0 break-words">{children}</dd>
		</div>
	);
}

export default CredentialDrawer;
